import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Award, Globe2, MapPin, ChevronRight, Trophy, Star, CheckCircle2, LockKeyhole, Loader2 } from "lucide-react";

// TopoJSON world atlas — public CDN
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function Passport() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    api.get("/academy/passport")
      .then((r) => setData(r.data))
      .finally(() => setBusy(false));
  }, []);

  const pinsByCountry = useMemo(() => {
    const map = {};
    (data?.pins || []).forEach((p) => {
      (map[p.country_code] = map[p.country_code] || []).push(p);
    });
    return map;
  }, [data]);

  const unlockedCodes = useMemo(() => {
    const s = new Set();
    (data?.pins || []).forEach((p) => { if (p.certified) s.add(p.country_code); });
    return s;
  }, [data]);

  if (busy) return <div className="max-w-6xl mx-auto px-5 py-24 text-center text-[#8B95A1]"><Loader2 className="inline w-5 h-5 animate-spin mr-2" />Chargement du passeport…</div>;
  const s = data?.stats || {};

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-14" data-testid="passport-page">
      <span className="chip-pink inline-flex items-center gap-1.5"><Globe2 className="w-3 h-3" /> Passeport geoVINUM</span>
      <h1 className="font-display text-4xl md:text-6xl text-[#1F3A5F] mt-4 leading-[1.02] font-medium">
        Votre <span className="text-[#E5276A]">tour du monde</span> du vin
      </h1>
      <p className="text-[#4A5568] mt-4 max-w-2xl font-light md:text-lg">
        Chaque certification débloque une région. Explorez la carte, découvrez ce qu&apos;il vous reste à conquérir.
      </p>

      {/* KPI Ribbon */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<Trophy className="w-4 h-4 text-emerald-600" />} label="Certifications" value={`${s.courses_certified}/${s.total_courses}`} pct={s.completion_pct} testId="pass-kpi-certs" />
        <Kpi icon={<Globe2 className="w-4 h-4 text-[#1F3A5F]" />} label="Pays débloqués" value={`${s.countries_unlocked}/${s.total_countries}`} testId="pass-kpi-countries" />
        <Kpi icon={<MapPin className="w-4 h-4 text-[#E5276A]" />} label="Régions" value={`${s.regions_unlocked}/${s.total_regions}`} testId="pass-kpi-regions" />
        <Kpi icon={<Star className="w-4 h-4 text-amber-500" />} label="Progression" value={`${s.completion_pct}%`} testId="pass-kpi-progress" />
      </div>

      {/* World map */}
      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 app-card overflow-hidden">
          <div className="border-b border-[#1F3A5F]/8 px-4 py-3 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[#8B95A1] font-bold">Vignoble mondial</div>
            <div className="flex items-center gap-3 text-[10px] text-[#8B95A1]">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Certifié</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E5276A] inline-block" /> Disponible</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#1F3A5F]/20 inline-block" /> À venir</span>
            </div>
          </div>
          <div className="relative" style={{ background: "linear-gradient(180deg, #F7FAFC 0%, #FBF7F3 100%)" }}>
            <ComposableMap
              projectionConfig={{ scale: 155 }}
              width={880}
              height={460}
              style={{ width: "100%", height: "auto" }}
              data-testid="passport-globe"
            >
              <ZoomableGroup center={[10, 30]} zoom={1.1}>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const iso = geo.properties?.ISO_A2 || geo.properties?.iso_a2 || "";
                      const hasPin = pinsByCountry[iso];
                      const isUnlocked = unlockedCodes.has(iso);
                      const fill = isUnlocked
                        ? "#10B98155"
                        : hasPin
                          ? "#FCE4ED"
                          : "#E7EAF0";
                      const stroke = isUnlocked ? "#10B981" : hasPin ? "#E5276A" : "#CBD5E1";
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={0.4}
                          style={{
                            default: { outline: "none" },
                            hover: { fill: isUnlocked ? "#10B98188" : "#F7E9C7", outline: "none", cursor: hasPin ? "pointer" : "default" },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
                {(data?.pins || []).filter((p) => typeof p.lat === "number" && typeof p.lng === "number").map((p) => (
                  <Marker
                    key={p.course_slug}
                    coordinates={[p.lng, p.lat]}
                    onMouseEnter={() => setHovered(p)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => setSelected(p)}
                    data-testid={`passport-pin-${p.course_slug}`}
                  >
                    <g style={{ cursor: "pointer" }}>
                      <circle r={p.certified ? 9 : 7} fill={p.certified ? "#10B981" : "#E5276A"} stroke="#fff" strokeWidth={2} />
                      {p.certified && <circle r={13} fill="none" stroke="#10B981" strokeWidth={1.5} opacity={0.4}>
                        <animate attributeName="r" from="10" to="18" dur="1.6s" repeatCount="indefinite" />
                        <animate attributeName="opacity" from="0.6" to="0" dur="1.6s" repeatCount="indefinite" />
                      </circle>}
                    </g>
                  </Marker>
                ))}
              </ZoomableGroup>
            </ComposableMap>
            {hovered && (
              <div className="absolute top-3 left-3 max-w-xs px-3 py-2 rounded-xl bg-white/95 backdrop-blur-sm shadow-md border border-[#1F3A5F]/10 pointer-events-none">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#E5276A]">{hovered.emoji} {hovered.region}</div>
                <div className="font-display text-sm text-[#1F3A5F] mt-0.5">{hovered.title}</div>
                <div className="text-[10px] text-[#8B95A1] mt-1">{hovered.certified ? "✓ Certifié" : "Cliquer pour commencer"}</div>
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="app-card p-5" data-testid="passport-sidebar">
          {selected ? (
            <div>
              <div className="text-4xl">{selected.emoji}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold mt-2">{selected.level_label} · {selected.country}</div>
              <h2 className="font-display text-2xl text-[#1F3A5F] mt-1">{selected.title}</h2>
              <p className="text-sm text-[#4A5568] mt-1">{selected.region}</p>
              {selected.certified ? (
                <div className="mt-4 inline-flex items-center gap-1.5 text-emerald-700 font-bold text-sm">
                  <CheckCircle2 className="w-4 h-4" /> Région certifiée
                </div>
              ) : (
                <div className="mt-4 inline-flex items-center gap-1.5 text-[#4A5568] text-sm">
                  <LockKeyhole className="w-4 h-4 text-[#E5276A]" /> Terminez le parcours pour débloquer
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to={`/courses/${selected.course_slug}`}
                  data-testid="passport-goto-course"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]"
                >
                  {selected.certified ? "Revoir le parcours" : "Commencer"} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ) : (
            <div>
              <div className="w-12 h-12 rounded-xl bg-[#FCE4ED] flex items-center justify-center">
                <Award className="w-6 h-6 text-[#E5276A]" />
              </div>
              <p className="mt-3 font-bold text-[#1F3A5F]">Cliquez sur un point</p>
              <p className="text-sm text-[#4A5568] mt-1">Chaque marqueur représente une région du monde à découvrir via un parcours Academy.</p>
              {!user && (
                <Link to="/register" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#E5276A] text-[#E5276A] text-sm font-bold hover:bg-[#FCE4ED]">
                  Créer un compte pour commencer <ChevronRight className="w-4 h-4" />
                </Link>
              )}
              <div className="mt-5 pt-5 border-t border-[#1F3A5F]/8">
                <div className="text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold mb-3">Régions débloquées</div>
                <div className="space-y-2 max-h-64 overflow-auto">
                  {(data?.pins || []).map((p) => (
                    <button
                      key={p.course_slug}
                      onClick={() => setSelected(p)}
                      data-testid={`passport-list-${p.course_slug}`}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                        p.certified ? "bg-emerald-50 hover:bg-emerald-100" : "hover:bg-[#FBF7F3]"
                      }`}
                    >
                      <span className="text-lg">{p.emoji}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-[#1F3A5F] truncate">{p.title}</div>
                        <div className="text-[10px] text-[#8B95A1]">{p.country} · {p.region}</div>
                      </div>
                      {p.certified ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <LockKeyhole className="w-3.5 h-3.5 text-[#8B95A1] shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, pct, testId }) {
  return (
    <div className="app-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold">{label}</span>
      </div>
      <div className="font-display text-3xl text-[#1F3A5F] mt-1.5 leading-none">{value}</div>
      {pct !== undefined && (
        <div className="mt-3 h-1.5 rounded-full bg-[#FCE4ED] overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#E5276A] to-emerald-500" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
